/**
 * MarkUp Component
 *
 * Renders the resolver's `payload.markup.{html, links}` inside a `Card`.
 *
 * Security (CWE-79): The HTML is sanitised on the server (bleach in
 * `backend/reporting/chart_resolver/markup.py`) AND again on the client
 * via DOMPurify before being passed to `dangerouslySetInnerHTML`. Defence
 * in depth — never trust either layer alone for stored XSS prevention.
 */

import DOMPurify from 'dompurify';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { ExternalLink } from 'lucide-react';
import { useIndicatorData } from './useIndicatorData';

const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'u', 'ul', 'ol', 'li', 'a',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'blockquote', 'code', 'pre', 'span', 'div',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
  ],
  ALLOWED_ATTR: ['href', 'title', 'target', 'rel', 'class'],
};

const SAFE_URL_PROTOCOLS = ['http:', 'https:', 'mailto:'];

function isSafeLinkUrl(url: string): boolean {
  try {
    // Anchor-relative or empty URLs are dropped by the caller.
    const parsed = new URL(url, window.location.origin);
    return SAFE_URL_PROTOCOLS.includes(parsed.protocol);
  } catch {
    return false;
  }
}

interface MarkUpComponentProps {
  indicatorId?: number;
}

export function MarkUpComponent({ indicatorId }: MarkUpComponentProps = {}) {
  const { data: live, loading } = useIndicatorData(indicatorId);

  const markup = live?.markup;
  const html = markup?.html || '';
  const links = markup?.links || [];

  if (loading && !markup) {
    return (
      <div className="flex items-center justify-center h-[180px] text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  if (!html && links.length === 0) {
    return (
      <div className="flex items-center justify-center h-[180px] text-muted-foreground text-sm">
        No content configured.
      </div>
    );
  }

  const safeHtml = html ? DOMPurify.sanitize(html, SANITIZE_CONFIG) : '';
  const safeLinks = links.filter((link) => link.url && isSafeLinkUrl(link.url));

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        {safeHtml && (
          <div
            className="prose prose-sm max-w-none dark:prose-invert"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: safeHtml }}
          />
        )}
        {safeLinks.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            {safeLinks.map((link, idx) => (
              <Button
                key={`${link.url}-${idx}`}
                variant="outline"
                size="sm"
                asChild
              >
                <a href={link.url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3 w-3 mr-1" />
                  {link.label || link.url}
                </a>
              </Button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
